import { Component, OnInit, Input } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { catchError, debounceTime, Subscription, throwError } from 'rxjs';
import { MacroNutrientData } from 'src/app/models/macro-nutrient-data';
import { SyncService } from 'src/app/services/sync-service';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-macronutrients',
  templateUrl: './macronutrients.component.html',
  styleUrls: ['./macronutrients.component.css'],
  standalone: false
})
export class MacronutrientsComponent implements OnInit {
  @Input() fieldId: string = '';
  @Input() farmId: string;
  
  @Input() fromDate: String;
  @Input() toDate: String;

  chartOption: EChartsOption = {};
  hasData: boolean = true;
  isLoading: boolean = true;

  macroNutrientData: MacroNutrientData = {
    cultivars: [],
    legends: [],
    n: [],
    p: [],
    k: [],
    ca: [],
    mg: [],
    s: []
  };

  private subscription: Subscription;

  constructor(
    private syncService: SyncService,
    private dataService: FieldDashboardDataService
  ) {
  }


  ngOnInit(): void {
    this.subscription = this.dataService.fieldData$.pipe(debounceTime(100)).subscribe(data => {
      if (data.fieldId && data.farmId && data.startDate && data.endDate) {
        let fromDate: string;
        let toDate: string;

        if (data.userChangedDates) {
          fromDate = formatDate(data.startDate, 'yyyy-MM-dd', 'en-ZA');
          toDate = formatDate(data.endDate, 'yyyy-MM-dd', 'en-ZA');
        } else if (data.season) {
          const startYear = parseInt(data.season.split('/')[0]);
          fromDate = `${startYear}-06-01`;
          toDate = `${startYear + 1}-05-31`;
        } else {
          return;
        }

        if (this.fieldId === data.fieldId && this.fromDate === fromDate && this.toDate === toDate) return;

        this.fieldId = data.fieldId;
        this.farmId = '2475';
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.populateData();
      }
    });
  }

  color = ['#FFB400', '#6355C7', '#5070dd', '#b6d634', '#505372', '#ff994d', '#0ca8df'];

  populateData() {
    this.isLoading = true;
    this.hasData = true;
    console.log("From date", this.fromDate);
    console.log("To date", this.toDate);
    this.syncService.getMacroNutrientStats(this.fieldId, this.fromDate, this.toDate).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.macroNutrientData = response;
      this.isLoading = false;
      this.loadChartData();
    })
  }

  loadChartData(): void {
    this.hasData = this.macroNutrientData.cultivars.length > 0;
    if (!this.hasData) return;
    this.chartOption = {
      title: {
        text: 'N, P, K, Ca, Mg & S',
        left: 0,
        top: 10,
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: this.macroNutrientData.legends,
        bottom: 0,
        itemGap: 25
      },
      grid: {
        left: '10%',
        right: '5%',
        bottom: '10%',
        top: '25%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: this.macroNutrientData.cultivars
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}'
        }
      },
      series: [
        { name: 'N/Ha', data: this.macroNutrientData.n, color: '#5470c6' },
        { name: 'P/Ha', data: this.macroNutrientData.p, color: '#91cc75' },
        { name: 'K/Ha', data: this.macroNutrientData.k, color: '#fac858' },
        { name: 'Ca/Ha', data: this.macroNutrientData.ca, color: '#ee6666' },
        { name: 'Mg/Ha', data: this.macroNutrientData.mg, color: '#73c0de' },
        { name: 'S/Ha', data: this.macroNutrientData.s, color: '#3ba272' }
      ].filter(s => s.data.some(v => v !== 0)).map(s => ({
        name: s.name, type: 'bar' as const, data: s.data, itemStyle: { color: s.color }
      }))
    };
  }
}
